import { Mail, Phone, MapPin, Instagram, Linkedin, Facebook, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import MagneticButton from "./MagneticButton";
import { company } from "../data/site";
import useSiteSettings from "../hooks/useSiteSettings";

export default function Footer() {
  const { getSetting } = useSiteSettings();
  
  const address = getSetting('company_address', company.address);
  const email = getSetting('company_email', company.email);
  const instagram = getSetting('social_instagram', '#');
  const linkedin = getSetting('social_linkedin', '#');
  const facebook = getSetting('social_facebook', '#');

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#080808] pt-24 pb-12 border-t border-white/5"
    >
      <div className="absolute -top-32 left-1/4 h-64 w-64 orange-glow opacity-60" />

      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Logo testId="footer-logo" className="-ml-1" />
          <p className="mt-6 text-white/55 max-w-md leading-relaxed text-[15px]">
            A trusted catalyst for Hyderabad’s dynamic building landscape. End-to-end
            approvals, liaison, and turnkey delivery — since {company.founded}.
          </p>

          <a
            href={`mailto:${email}`}
            className="mt-8 inline-flex items-center gap-2 font-display text-2xl md:text-3xl text-white hover:text-[#C96A2B] transition-colors"
          >
            {email}
            <ArrowUpRight size={20} />
          </a>

          <div className="mt-8 flex gap-3">
            {[
              { Icon: Instagram, href: instagram },
              { Icon: Linkedin, href: linkedin },
              { Icon: Facebook, href: facebook }
            ].map(({ Icon, href }, i) => (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="social link"
                data-testid={`footer-social-${i}`}
                className="h-11 w-11 grid place-items-center rounded-full border border-white/10 hover:border-[#C96A2B] hover:text-[#C96A2B] transition-colors"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3">
          <p className="eyebrow mb-5">Navigate</p>
          <ul className="space-y-3 text-white/65">
            {[
              ["Home", "/"],
              ["About", "/about"],
              ["Services", "/services"],
              ["Projects", "/projects"],
              ["Clients", "/clients"],
              ["Consultation", "/consultation"],
              ["Contact", "/contact"],
            ].map(([l, to]) => (
              <li key={l}>
                <Link to={to} className="hover:text-[#C96A2B] transition-colors">
                  {l}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-4">
          <p className="eyebrow mb-5">Reach Us</p>
          <ul className="space-y-4 text-white/65">
            <li className="flex gap-3">
              <MapPin size={18} className="mt-1 text-[#C96A2B] shrink-0" />
              <div>
                <p className="text-white/50 text-sm max-w-xs">{address}</p>
                <a
                  href={`mailto:${email}`}
                  className="inline-block text-white/50 hover:text-white transition-colors mt-2"
                >
                  {email}
                </a>
              </div>
            </li>
            <li className="flex gap-3">
              <Phone size={18} className="mt-1 text-[#C96A2B] shrink-0" />
              <div>
                <a
                  href={`tel:${company.phone.replace(/\s/g, "")}`}
                  className="block hover:text-white transition-colors"
                >
                  {company.phone}
                </a>
                <a
                  href={`tel:${company.phoneAlt.replace(/\s/g, "")}`}
                  className="block text-sm text-white/45 hover:text-white transition-colors"
                >
                  {company.phoneAlt}
                </a>
              </div>
            </li>
            <li className="flex gap-3">
              <Mail size={18} className="mt-1 text-[#C96A2B] shrink-0" />
              <a href={`mailto:${email}`} className="hover:text-white transition-colors">
                {email}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/40 tracking-wider">
        <p>© {new Date().getFullYear()} HOFLICH · Building Solutions. All rights reserved.</p>
        <p>Crafted with cinematic precision.</p>
      </div>
    </footer>
  );
}
